/**
 * ORIVON core (global ES module, docs/IMPLEMENTATION_PLAN.md §2.6).
 * Loaded on every page by snippets/orivon-head.liquid as <script type="module">, after Horizon's importmap.
 * Port of the page-level behaviours in design/assets/js/orivon.js, renamed to the theme's data hooks:
 *
 *   [data-o-reveal] [data-o-reveal-delay="120"]   Fade/rise in once when scrolled into view (CSS: orivon-base.css §21).
 *   [data-o-reveal-stagger]                       On a parent: every child reveals, 60ms apart.
 *   [data-o-sweep-loop="8000"]                    Light sweep across .o-sweep (the element or its descendants) every N ms while in view.
 *   [data-o-lamp]                                 Inspection lamp: the pointer position lights the .o-hood inside (fine pointers only).
 *   details[data-o-footer-accordion]              Open from 768px, collapsed (tap to open) below.
 *   ?ph=1                                         html.o-show-placeholders: outlines every [data-o-placeholder]
 *                                                 (the Theme settings QA checkbox does the same from orivon-head).
 *   ?static=1, HeadlessChrome                     html.o-static: final states, no transitions (screenshots / QA).
 *   #cart-drawer                                  Asks Horizon to re-measure the pinned checkout footer when the
 *                                                 open drawer's content changes (first add to an empty cart).
 *
 * Reveals show everything at once in the theme editor, with reduced motion, in static mode and without
 * IntersectionObserver. Sweeps and the lamp never run with reduced motion. Nothing here reads window.scrollY
 * (from 990px Horizon scrolls .page-wrapper; IntersectionObserver works with any scroll container).
 * Sections re-rendered by the theme editor are re-initialised on `shopify:section:load`.
 */

const root = document.documentElement;
const params = new URLSearchParams(window.location.search);
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const mediumUp = window.matchMedia('(min-width: 48em)');

const isStatic = /HeadlessChrome/.test(navigator.userAgent) || params.get('static') === '1';
const isDesignMode = () => Boolean(window.Shopify?.designMode) || root.classList.contains('shopify-design-mode');
const hasObserver = 'IntersectionObserver' in window;

if (params.get('ph') === '1') root.classList.add('o-show-placeholders');
if (isStatic) root.classList.add('o-static');

/** @param {ParentNode} scope @param {string} selector @returns {HTMLElement[]} */
const all = (scope, selector) => {
  /** @type {HTMLElement[]} */
  const found = [...scope.querySelectorAll(selector)];
  if (scope instanceof HTMLElement && scope.matches(selector)) found.unshift(scope);
  return found;
};

/** rAF-throttled callback (runs at most once per frame). @param {(...args: any[]) => void} fn */
const perFrame = (fn) => {
  let frame = 0;
  /** @type {any[]} */
  let last = [];
  return (/** @type {any[]} */ ...args) => {
    last = args;
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      fn(...last);
    });
  };
};

/* ---------- reveal ---------- */

/** @type {IntersectionObserver | null} */
let revealObserver = null;
/** Observed target → the [data-o-reveal] elements it reveals (a rail reveals all of its cards together). */
const revealGroups = new Map();

/** @param {IntersectionObserverEntry[]} entries */
function onRevealIntersect(entries) {
  for (const entry of entries) {
    // Reveal when in view, or when already scrolled past (e.g. a jump link or a restored scroll position).
    if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) continue;
    revealGroups.get(entry.target)?.forEach((/** @type {HTMLElement} */ el) => el.classList.add('is-revealed'));
    revealGroups.delete(entry.target);
    revealObserver?.unobserve(entry.target);
  }
}

/** @param {ParentNode} scope */
function initReveal(scope) {
  for (const parent of all(scope, '[data-o-reveal-stagger]')) {
    [...parent.children].forEach((child, index) => {
      if (!(child instanceof HTMLElement)) return;
      if (!child.hasAttribute('data-o-reveal')) child.setAttribute('data-o-reveal', '');
      child.style.setProperty('--reveal-delay', `${index * 60}ms`);
    });
  }

  const elements = all(scope, '[data-o-reveal]:not(.is-revealed)');
  for (const el of elements) {
    const delay = parseInt(el.getAttribute('data-o-reveal-delay') || '', 10);
    if (delay) el.style.setProperty('--reveal-delay', `${delay}ms`);
  }

  if (isStatic || isDesignMode() || reducedMotion.matches || !hasObserver) {
    elements.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  // Forget targets the theme editor removed.
  for (const target of revealGroups.keys()) {
    if (!target.isConnected) {
      revealObserver?.unobserve(target);
      revealGroups.delete(target);
    }
  }

  revealObserver ??= new IntersectionObserver(onRevealIntersect, { rootMargin: '0px 0px -6% 0px' });
  for (const el of elements) {
    const target = el.parentElement?.closest('.o-rail, .o-carousel__track, .o-bottle-key, .o-tabs__list') || el;
    let group = revealGroups.get(target);
    if (!group) {
      group = new Set();
      revealGroups.set(target, group);
      revealObserver.observe(target);
    }
    group.add(el);
  }
}

/* ---------- light sweep loop ---------- */

const sweepLoops = new WeakSet();

/** @param {HTMLElement} el */
function startSweepLoop(el) {
  if (sweepLoops.has(el)) return;
  sweepLoops.add(el);
  if (reducedMotion.matches || isStatic) return;

  const every = parseInt(el.getAttribute('data-o-sweep-loop') || '', 10) || 8000;
  const targets = el.classList.contains('o-sweep') ? [el] : all(el, '.o-sweep');
  if (!targets.length) return;

  let visible = !hasObserver;
  /** @type {IntersectionObserver | null} */
  let observer = null;
  if (hasObserver) {
    observer = new IntersectionObserver((entries) => entries.forEach((entry) => (visible = entry.isIntersecting)), {
      threshold: 0.3,
    });
    observer.observe(el);
  }

  const sweep = () => {
    for (const target of targets) {
      target.classList.remove('is-sweeping');
      void target.offsetWidth; // restart the transition
      target.classList.add('is-sweeping');
      window.setTimeout(() => target.classList.remove('is-sweeping'), 1000);
    }
  };
  const tick = () => {
    if (!el.isConnected) {
      window.clearInterval(timer);
      observer?.disconnect();
      return;
    }
    if (visible && !document.hidden && !reducedMotion.matches) sweep();
  };

  window.setTimeout(tick, 900);
  const timer = window.setInterval(tick, every);
}

/* ---------- inspection lamp ---------- */

const lamps = new WeakSet();

/** @param {HTMLElement} el */
function startLamp(el) {
  if (lamps.has(el)) return;
  lamps.add(el);
  if (!finePointer.matches || reducedMotion.matches || isStatic) return;

  /** @type {HTMLElement} */
  const hood = el.querySelector('.o-hood') || el;
  const move = perFrame((/** @type {PointerEvent} */ event) => {
    const rect = hood.getBoundingClientRect();
    hood.style.setProperty('--mx', `${event.clientX - rect.left}px`);
    hood.style.setProperty('--my', `${event.clientY - rect.top}px`);
  });
  el.addEventListener('pointermove', move, { passive: true });
  el.addEventListener('pointerleave', () => hood.style.setProperty('--mx', '-999px'), { passive: true });
}

/* ---------- footer accordions ---------- */

const footerSummaries = new WeakSet();

function syncFooterAccordions() {
  for (const details of document.querySelectorAll('details[data-o-footer-accordion]')) {
    if (details instanceof HTMLDetailsElement) details.open = mediumUp.matches;
  }
}

/** @param {ParentNode} scope */
function initFooterAccordions(scope) {
  const items = all(scope, 'details[data-o-footer-accordion]');
  if (!items.length) return;
  for (const details of items) {
    if (!(details instanceof HTMLDetailsElement)) continue;
    details.open = mediumUp.matches;
    const summary = details.querySelector(':scope > summary');
    if (summary && !footerSummaries.has(summary)) {
      footerSummaries.add(summary);
      // Columns stay open from 768px: ignore the toggle there.
      summary.addEventListener('click', (event) => {
        if (mediumUp.matches) event.preventDefault();
      });
    }
  }
}

mediumUp.addEventListener('change', syncFooterAccordions);

/* ---------- cart drawer: keep the checkout footer pinned ---------- */

/*
 * Horizon (assets/cart-drawer.js) decides on open whether the drawer's summary (total + checkout button) stays
 * pinned (dialog[cart-summary-sticky]). On the first add to an EMPTY cart it measures before the drawer has been
 * morphed to show the lines, finds no summary and records "false", so on phones the checkout button sits below the
 * fold until the next cart change. Whenever the open drawer's content changes while it is unpinned, ask Horizon to
 * measure again: cart-drawer.js re-runs its own check on `cart-section:restored` (it stays unpinned when the
 * summary really is taller than half the drawer). Attribute changes are not observed, so this cannot loop.
 */
const watchedDrawers = new WeakSet();

function watchCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || watchedDrawers.has(drawer) || !('MutationObserver' in window)) return;
  watchedDrawers.add(drawer);
  const remeasure = perFrame(() => {
    const dialog = drawer.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    if (dialog.getAttribute('cart-summary-sticky') !== 'false' || !dialog.querySelector('.cart-drawer__summary')) return;
    dialog.querySelector('cart-drawer-component')?.dispatchEvent(new Event('cart-section:restored', { bubbles: true }));
  });
  new MutationObserver(remeasure).observe(drawer, { childList: true, subtree: true });
}

/* ---------- boot ---------- */

/** Initialises every ORIVON behaviour inside `scope` (the document, or a re-rendered section). @param {ParentNode} [scope] */
export function init(scope = document) {
  initReveal(scope);
  all(scope, '[data-o-sweep-loop]').forEach(startSweepLoop);
  all(scope, '[data-o-lamp]').forEach(startLamp);
  initFooterAccordions(scope);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => (init(), watchCartDrawer()), { once: true });
} else {
  init();
  watchCartDrawer();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) init(event.target);
  watchCartDrawer();
});
